/* ============================================================
   Impact Stats — section-impact-stats.js
   Scroll-in animation + count-up for numeric stats. No deps.
   ============================================================ */
(function () {
  'use strict';

  const SELECTOR = '[data-impact-stats]';
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Parse a stat value into { prefix, number, suffix } or null if non-numeric.
     "100%" -> { prefix:"", number:100, suffix:"%" }
     "0"    -> { prefix:"", number:0,   suffix:"" }
     "CO2"  -> null (doesn't start with a digit)
     "$1.2M"-> { prefix:"$", number:1.2, suffix:"M" } */
  function parseStat(raw) {
    if (!raw) return null;
    const match = raw.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
    if (!match) return null;
    // Only animate when the numeric part is at/near the start (prefix is short,
    // e.g. a currency symbol). If a letter leads (like "CO2"), skip.
    const prefix = match[1];
    if (/[a-zA-Z]/.test(prefix)) return null;
    return {
      prefix: prefix,
      number: parseFloat(match[2]),
      decimals: (match[2].split('.')[1] || '').length,
      suffix: match[3]
    };
  }

  function animateCount(el, parsed, duration) {
    const startTime = performance.now();
    const target = parsed.number;

    function frame(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const current = (eased * target).toFixed(parsed.decimals);
      el.textContent = parsed.prefix + current + parsed.suffix;
      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = parsed.prefix + target.toFixed(parsed.decimals) + parsed.suffix;
      }
    }
    requestAnimationFrame(frame);
  }

  function runCountUp(root) {
    if (root.dataset.animateNumbers !== 'true' || prefersReduced) return;
    const values = root.querySelectorAll('[data-stat-value]');
    values.forEach(function (el) {
      const parsed = parseStat(el.getAttribute('data-stat-value'));
      if (parsed) {
        el.textContent = parsed.prefix + (0).toFixed(parsed.decimals) + parsed.suffix;
        animateCount(el, parsed, 1400);
      }
    });
  }

  function reveal(root) {
    root.classList.add('is-in-view');
    runCountUp(root);
  }

  function setup(root) {
    const animation = root.dataset.animation;
    const needsObserver =
      (animation && animation !== 'none' && !prefersReduced) ||
      (root.dataset.animateNumbers === 'true' && !prefersReduced);

    if (!needsObserver || !('IntersectionObserver' in window)) {
      reveal(root);
      return;
    }

    const io = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(root);
    root.__stObserver = io;
  }

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach(function (el) {
      if (!el.__stInitialized) {
        el.__stInitialized = true;
        setup(el);
      }
    });
  }

  function destroy(el) {
    if (el && el.__stObserver) {
      el.__stObserver.disconnect();
      el.__stObserver = null;
      el.__stInitialized = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* ---------- Shopify Theme Editor ---------- */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  document.addEventListener('shopify:section:unload', function (e) {
    const el = e.target.querySelector(SELECTOR);
    if (el) destroy(el);
  });

  document.addEventListener('shopify:section:select', function (e) {
    const el = e.target.querySelector(SELECTOR);
    if (el) reveal(el);
  });

  document.addEventListener('shopify:block:select', function (e) {
    const el = e.target.closest(SELECTOR);
    if (el) el.classList.add('is-in-view');
  });
})();
